import { useValue } from "@legendapp/state/react";
import { ChatModeSchema } from "@repo/shared/types/chat-mode";
import {
  useCommandPalette,
  useRegisterActions,
} from "@repo/ui/lib/actions/action-registry";
import { SHORTCUTS } from "@repo/ui/lib/actions/shortcuts";
import type { Action, SubPageOption } from "@repo/ui/lib/actions/types";
import { useRouterState } from "@tanstack/react-router";

import {
  CHAT_MODE_ICONS,
  CHAT_MODE_LABELS,
} from "~/components/app/chat/chat-mode-selector";
import {
  isChatPaneRoute,
  isThreadRoute,
} from "~/components/app/chat/chat-route";
import { chat$, cycleChatMode } from "~/stores/chat";

/**
 * Registers global command palette actions for the AI chat panel.
 * Active on dataset and board pages — unregistered elsewhere.
 */
export const ChatActions = () => {
  const isOpen = useValue(chat$.isOpen);
  const chatMode = useValue(chat$.mode);
  const palette = useCommandPalette();

  const isChatRoute = useRouterState({
    select: (s) => isChatPaneRoute(s.location.pathname),
  });

  // The full-page thread view isn't a chat-pane route, but it renders the same
  // chat input, so the mode-cycle shortcut should work there too.
  const isThreadPage = useRouterState({
    select: (s) => isThreadRoute(s.location.pathname),
  });

  const chatModeOptions: SubPageOption[] = ChatModeSchema.options.map(
    (mode) => ({
      id: `chat-mode-${mode}`,
      name: CHAT_MODE_LABELS[mode],
      icon: CHAT_MODE_ICONS[mode],
      checked: chatMode === mode,
      perform: () => chat$.mode.set(mode),
    }),
  );

  useRegisterActions(
    [
      // The AI pane toggle only makes sense where the pane exists; the
      // full-page thread view intentionally hides it.
      ...(isChatRoute
        ? ([
            {
              id: "toggle-ai-chat",
              name: "Toggle AI chat",
              group: "AI",
              icon: isOpen ? "pane-right-fill" : "pane-right",
              shortcut: SHORTCUTS["toggle-right-pane"].shortcut,
              perform: chat$.isOpen.toggle,
              priority: 90,
            },
          ] satisfies Action[])
        : []),
      // Mode cycling works anywhere the chat input is rendered, including the
      // full-page thread view.
      ...(isChatRoute || isThreadPage
        ? ([
            {
              id: "cycle-chat-mode",
              name: "Change agent mode...",
              group: "AI",
              icon: CHAT_MODE_ICONS[chatMode],
              shortcut: SHORTCUTS["cycle-chat-mode"].shortcut,
              perform: () => palette.showPage("cycle-chat-mode"),
              shortcutPerform: cycleChatMode,
              // Shift+Tab only cycles the mode while the chat input is focused;
              // elsewhere it falls through to normal reverse-tab navigation.
              shortcutWhen: (e) =>
                e.target instanceof HTMLElement &&
                e.target.closest(".chat-editor") !== null,
              subPage: {
                placeholder: "Select agent mode...",
                icon: CHAT_MODE_ICONS[chatMode],
                options: chatModeOptions,
              },
              priority: 90,
            },
          ] satisfies Action[])
        : []),
    ],
    [isChatRoute, isThreadPage, isOpen, chatMode],
  );

  return null;
};
